import { useEffect, useRef } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"

import type { FeedbackOrigin, FeedbackState, PainLevel } from "@productlane/db"
import type { ReplyStateFilter } from "@productlane/zero/queries"
import { clientSafeThreadFilters } from "@productlane/zero/queries"

import {
	deserializeNegations,
	NEGATION_PARAM,
	serializeNegations,
} from "@/components/filters"
import { useThreadsFilterState } from "./threads-filter-state"

const NULL_TOKEN = "_null"

const FILTER_PARAM_KEYS = {
	assigneeIds: "assignees",
	origins: "origins",
	portalInstanceIds: "portals",
	emailAddressIds: "inboxes",
	tagIds: "tags",
	projectIds: "projects",
	issueIds: "issues",
	painLevels: "priorities",
	creatorIds: "creators",
	companyIds: "companies",
	personIds: "people",
	statuses: "statuses",
	replyStates: "response",
	slackWorkspaceIds: "slack-workspaces",
	slackChannelIds: "slack-channels",
} as const

type FilterStateKey = keyof typeof FILTER_PARAM_KEYS

const ALL_PARAM_VALUES = [
	...Object.values(FILTER_PARAM_KEYS),
	NEGATION_PARAM,
] as const
const FILTER_KEY_SET = new Set<string>(ALL_PARAM_VALUES)

function serializeValues(values: Array<string | null>): string {
	return values.map((v) => (v === null ? NULL_TOKEN : v)).join(",")
}

function deserializeValues(param: string): Array<string | null> {
	if (!param) return []
	return param.split(",").map((v) => (v === NULL_TOKEN ? null : v))
}

function buildFilterParams(): URLSearchParams {
	const state = useThreadsFilterState.getState()
	const params = new URLSearchParams()

	const entries: Array<[FilterStateKey, Array<string | null>]> = [
		["assigneeIds", state.assigneeIds],
		["origins", state.origins],
		["portalInstanceIds", state.portalInstanceIds],
		["emailAddressIds", state.emailAddressIds],
		["tagIds", state.tagIds],
		["projectIds", state.projectIds],
		["issueIds", state.issueIds],
		["painLevels", state.painLevels],
		["creatorIds", state.creatorIds],
		["companyIds", state.companyIds],
		["personIds", state.personIds],
		["statuses", state.statuses],
		["replyStates", state.replyStates],
		["slackWorkspaceIds", state.slackWorkspaceIds],
		["slackChannelIds", state.slackChannelIds],
	]

	for (const [stateKey, values] of entries) {
		if (values.length > 0) {
			params.set(FILTER_PARAM_KEYS[stateKey], serializeValues(values))
		}
	}

	const negated = serializeNegations(state.negations, FILTER_PARAM_KEYS)
	if (negated) {
		params.set(NEGATION_PARAM, negated)
	}

	return params
}

/**
 * Two-way syncs the thread filter state with URL search params.
 *
 * On mount: if URL has filter params, they take precedence and are applied to
 * the Zustand store. Otherwise the current store state is written to the URL.
 *
 * After mount: any filter state change is reflected in the URL.
 */
export function useFilterUrlSync() {
	const search = useSearch({ strict: false })
	const navigate = useNavigate()
	const isInitialised = useRef(false)

	// On mount: read URL → store (URL wins if it has filter params)
	useEffect(() => {
		if (isInitialised.current) return
		isInitialised.current = true

		const hasFilterParams = ALL_PARAM_VALUES.some(
			(key) => search[key] !== undefined,
		)

		if (hasFilterParams) {
			// URL → store
			const assignees = deserializeValues(
				search[FILTER_PARAM_KEYS.assigneeIds] ?? "",
			)
			const origins = deserializeValues(search[FILTER_PARAM_KEYS.origins] ?? "")
			const portals = deserializeValues(
				search[FILTER_PARAM_KEYS.portalInstanceIds] ?? "",
			)
			const inboxes = deserializeValues(
				search[FILTER_PARAM_KEYS.emailAddressIds] ?? "",
			)
			const tags = deserializeValues(search[FILTER_PARAM_KEYS.tagIds] ?? "")
			const projects = deserializeValues(
				search[FILTER_PARAM_KEYS.projectIds] ?? "",
			)
			const issues = deserializeValues(search[FILTER_PARAM_KEYS.issueIds] ?? "")
			const priorities = deserializeValues(
				search[FILTER_PARAM_KEYS.painLevels] ?? "",
			)
			const creators = deserializeValues(
				search[FILTER_PARAM_KEYS.creatorIds] ?? "",
			)
			const companies = deserializeValues(
				search[FILTER_PARAM_KEYS.companyIds] ?? "",
			)
			const people = deserializeValues(
				search[FILTER_PARAM_KEYS.personIds] ?? "",
			)
			const statuses = deserializeValues(
				search[FILTER_PARAM_KEYS.statuses] ?? "",
			)
			const replyStates = deserializeValues(
				search[FILTER_PARAM_KEYS.replyStates] ?? "",
			)
			const slackWorkspaces = deserializeValues(
				search[FILTER_PARAM_KEYS.slackWorkspaceIds] ?? "",
			)
			const slackChannels = deserializeValues(
				search[FILTER_PARAM_KEYS.slackChannelIds] ?? "",
			)

			useThreadsFilterState.setState(
				clientSafeThreadFilters({
					negations: deserializeNegations(
						search[NEGATION_PARAM] ?? "",
						FILTER_PARAM_KEYS,
					),
					assigneeIds: assignees,
					origins: origins.filter((v): v is FeedbackOrigin => v !== null),
					portalInstanceIds: portals,
					emailAddressIds: inboxes,
					tagIds: tags,
					projectIds: projects,
					issueIds: issues,
					painLevels: priorities.filter((v): v is PainLevel => v !== null),
					creatorIds: creators.filter((v): v is string => v !== null),
					companyIds: companies,
					personIds: people,
					statuses: statuses.filter((v): v is FeedbackState => v !== null),
					replyStates: replyStates.filter(
						(v): v is ReplyStateFilter =>
							v === "NEEDS_RESPONSE" || v === "REPLIED",
					),
					slackWorkspaceIds: slackWorkspaces.filter(
						(v): v is string => v !== null,
					),
					slackChannelIds: slackChannels.filter((v): v is string => v !== null),
				}),
			)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Subscribe to the Zustand store directly so we don't depend on
	// React render cycles or stale closures.
	useEffect(() => {
		const unsub = useThreadsFilterState.subscribe(() => {
			if (!isInitialised.current) return

			const filterParams = buildFilterParams()

			void navigate({
				to: ".",
				search: (prev) => ({
					...Object.fromEntries(
						Object.entries(prev).filter(([key]) => !FILTER_KEY_SET.has(key)),
					),
					...Object.fromEntries(filterParams),
				}),
				replace: true,
			})
		})
		return unsub
	}, [navigate])
}
